import React, { Suspense, useEffect, useState } from "react";
import { Loader } from "./controls";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import "./styles/App.scss";
import "./styles/Style.scss";
import "./styles/rtl.scss";
import SiteRoute from "./pages/SiteRoute";
import ServerError from "./pages/ServerError";
import { Login, Forgot } from "./components/auth";
import { API, action, controller } from "./api/api";
import Toast from "layout/Toast";
import { lsDelete, lsSave } from "hooks";
import { CACHE_VERSION } from "config/version";
import { AppContext } from "./context";
import ThemeLocaleProvider from "./i18n/ThemeLocaleProvider";
import i18n from "./i18n/i18n";

const App = () => {
  const [loader, setLoader] = useState(false);
  const [authUser, setAuthUser] = useState();
  const [bizContext, setBizContext] = useState({
    orgId: authUser?.orgId || 0,
    companyId: "",
    outletId: "",
  });
  const [selCompany, setSelCompany] = useState(null);
  const [settings, setSettings] = useState({});
  const [access, setAccess] = useState({});
  const [org, setOrg] = useState({});
  const [serverError, setServerError] = useState(null);

  // Listen for server-error events from axios interceptor
  useEffect(() => {
    const handleServerError = (e) => setServerError(e.detail?.type || "network");
    window.addEventListener("server-error", handleServerError);
    return () => window.removeEventListener("server-error", handleServerError);
  }, []);

  // ----- Clear browser local storage data base on the version changes -----
  useEffect(() => {
    const savedVersion = localStorage.getItem("cache_version");
    if (savedVersion !== CACHE_VERSION) {
      console.log("Cache version changed, clearing localStorage");
      localStorage.clear();
      localStorage.setItem("cache_version", CACHE_VERSION);
    }
  }, []);

  // Fetch auth user
  useEffect(() => {
    const fetchAuthUser = async () => {
      try {
        await API(controller.Auth + action.GetAuthUser).get((res, err) => {
          if (res && res.status === 200 && res.result) {
            if (res.result.defaultLangCode) {
              i18n.changeLanguage(res.result.defaultLangCode);
            }
            setAuthUser(res.result);
            res.result?.org
              ? setOrg({ ...res.result?.org })
              : console.error("Org information not found!");
          } else setAuthUser(null);
        });
      } catch (error) {
        console.error("Auth user fetch error:", error);
        setAuthUser(null);
      }
    };
    fetchAuthUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshSettings = () => {
    if (authUser?.id && selCompany) {
      API(controller.Org + action.GetSettings).getById(selCompany, (res) => {
        if (res?.status === 200) {
          setSettings({ ...res?.settings });
          setAccess({ ...res?.featureAccess });
        }
      });
      API(controller.SellReg + action.GetCashierOpenReg).getById(
        selCompany,
        (res) => {
          if (res && res.status === 200) {
            if (res.result) lsSave("activeReg", { ...res.result });
            else lsDelete("activeReg");
          }
        },
      );
    }
  };

  useEffect(() => {
    refreshSettings();
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, selCompany]);

  return (
    <Suspense fallback={<Loader show />}>
      <ThemeLocaleProvider uiTemplate={settings?.uiTemplate}>
        <AppContext.Provider
          value={{
            loader,
            setLoader,
            authUser,
            setAuthUser,
            settings,
            access,
            org,
            setSelCompany,
            selCompany,
            bizContext,
            setBizContext,
            refreshSettings,
          }}
        >
          <Loader show={loader} />
          {serverError && (
            <ServerError
              type={serverError}
              onRetry={() => {
                setServerError(null);
                window.location.reload();
              }}
              onDismiss={() => setServerError(null)}
            />
          )}
          <Router>
            <Routes>
              <Route
                path="/*"
                element={authUser ? <SiteRoute /> : !loader && <Auth />}
              />
            </Routes>
          </Router>
          <Toast />
        </AppContext.Provider>
      </ThemeLocaleProvider>
    </Suspense>
  );
};

const Auth = () => (
  <>
    <Routes>
      <Route path="/" element={<Navigate to="auth" />} />
      <Route path="/auth">
        <Route path="forgot" element={<Forgot />} />
        <Route index element={<Login />} />
      </Route>
    </Routes>
  </>
);

export default App;
